import { useState, useEffect, useCallback, useRef } from 'react';
import { adminService } from '../services/adminService';
import { AdminProduct, AdminProductFilter, AdminProductStatus } from '../models';
import { AdminProductImage } from '../models/product';

const PAGE_SIZE = 10;

interface SortOption {
  label: string;
  sortBy?: string;
  sortDirection?: string;
}

export const ADMIN_PRODUCT_SORT_OPTIONS: SortOption[] = [
  { label: 'Nome A-Z', sortBy: 'name', sortDirection: 'asc' },
  { label: 'Nome Z-A', sortBy: 'name', sortDirection: 'desc' },
  { label: 'Menor preço', sortBy: 'price', sortDirection: 'asc' },
  { label: 'Maior preço', sortBy: 'price', sortDirection: 'desc' },
  { label: 'Mais recentes', sortBy: 'createdAt', sortDirection: 'desc' },
];

interface AdminProductListState {
  products: AdminProduct[];
  total: number;
  totalPages: number;
  isLoading: boolean;
  error: string | null;

  page: number;
  searchInput: string;
  categoryId: number | undefined;
  status: AdminProductStatus;
  sortIndex: number;
  gender: number | undefined;
  variationTypeId: number | undefined;
  variationValue: string | undefined;
  supplierId: number | undefined;
  collectionId: number | undefined;

  setPage: (page: number) => void;
  handleSearchChange: (value: string) => void;
  handleCategoryChange: (id: number | undefined) => void;
  handleStatusChange: (status: AdminProductStatus) => void;
  handleSortChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleGenderChange: (gender: number | undefined) => void;
  handleVariationTypeChange: (variationTypeId: number | undefined) => void;
  handleVariationValueChange: (variationValue: string | undefined) => void;
  handleSupplierChange: (supplierId: number | undefined) => void;
  handleCollectionChange: (collectionId: number | undefined) => void;
  clearFilters: () => void;
  updateProductImages: (productId: number, images: AdminProductImage[]) => void;

  hasFilters: boolean;
}

export function useAdminProductList(): AdminProductListState {
  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [page, setPageState] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [committedSearch, setCommittedSearch] = useState('');
  const [categoryId, setCategoryId] = useState<number | undefined>(undefined);
  const [status, setStatus] = useState<AdminProductStatus>('all');
  const [sortIndex, setSortIndex] = useState(0);
  const [gender, setGender] = useState<number | undefined>(undefined);
  const [variationTypeId, setVariationTypeId] = useState<number | undefined>(undefined);
  const [variationValue, setVariationValue] = useState<string | undefined>(undefined);
  const [supplierId, setSupplierId] = useState<number | undefined>(undefined);
  const [collectionId, setCollectionId] = useState<number | undefined>(undefined);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const fetchProducts = useCallback(async (filter: AdminProductFilter) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await adminService.getProducts(filter);
      setProducts(data.items);
      setTotal(data.count);
    } catch {
      setError('Erro ao carregar produtos. Tente novamente.');
      setProducts([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const selectedSort = ADMIN_PRODUCT_SORT_OPTIONS[sortIndex];
    const filter: AdminProductFilter = {
      pageNumber: page,
      pageSize: PAGE_SIZE,
      search: committedSearch || undefined,
      categoryId: categoryId,
      active: status === 'all' ? undefined : status === 'active',
      onlyInStock: true,
      sortBy: selectedSort.sortBy,
      sortDirection: selectedSort.sortDirection,
      gender,
      variationTypeId,
      variationValue,
      supplierId,
      collectionId,
    };
    fetchProducts(filter);
  }, [page, committedSearch, categoryId, status, sortIndex, gender, variationTypeId, variationValue, supplierId, collectionId, fetchProducts]);

  const setPage = (newPage: number) => {
    setPageState(newPage);
  };

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setCommittedSearch(value);
      setPageState(1);
    }, 300);
  };

  const handleCategoryChange = (id: number | undefined) => {
    setCategoryId(id);
    setPageState(1);
  };

  const handleStatusChange = (newStatus: AdminProductStatus) => {
    setStatus(newStatus);
    setPageState(1);
  };

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSortIndex(Number(e.target.value));
    setPageState(1);
  };

  const handleGenderChange = (newGender: number | undefined) => {
    setGender(newGender);
    setPageState(1);
  };

  const handleVariationTypeChange = (newVariationTypeId: number | undefined) => {
    setVariationTypeId(newVariationTypeId);
    setVariationValue(undefined);
    setPageState(1);
  };

  const handleVariationValueChange = (newVariationValue: string | undefined) => {
    setVariationValue(newVariationValue);
    setPageState(1);
  };

  const handleSupplierChange = (newSupplierId: number | undefined) => {
    setSupplierId(newSupplierId);
    setPageState(1);
  };

  const handleCollectionChange = (newCollectionId: number | undefined) => {
    setCollectionId(newCollectionId);
    setPageState(1);
  };

  const clearFilters = () => {
    setSearchInput('');
    setCommittedSearch('');
    setCategoryId(undefined);
    setStatus('all');
    setSortIndex(0);
    setGender(undefined);
    setVariationTypeId(undefined);
    setVariationValue(undefined);
    setSupplierId(undefined);
    setCollectionId(undefined);
    setPageState(1);
  };

  const updateProductImages = useCallback((productId: number, images: AdminProductImage[]) => {
    setProducts((prev) =>
      prev.map((p) => (p.id === productId ? { ...p, images } : p))
    );
  }, []);

  const hasFilters =
    committedSearch !== '' ||
    categoryId !== undefined ||
    status !== 'all' ||
    sortIndex !== 0 ||
    gender !== undefined ||
    variationTypeId !== undefined ||
    variationValue !== undefined ||
    supplierId !== undefined ||
    collectionId !== undefined;

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return {
    products,
    total,
    totalPages,
    isLoading,
    error,

    page,
    searchInput,
    categoryId,
    status,
    sortIndex,
    gender,
    variationTypeId,
    variationValue,
    supplierId,
    collectionId,

    setPage,
    handleSearchChange,
    handleCategoryChange,
    handleStatusChange,
    handleSortChange,
    handleGenderChange,
    handleVariationTypeChange,
    handleVariationValueChange,
    handleSupplierChange,
    handleCollectionChange,
    clearFilters,
    updateProductImages,

    hasFilters,
  };
}
